import { useEffect, useRef, useState } from 'react'

import { useSelector } from 'store/hooks'
import { isIncomingInFlightTransfer } from 'utils/certificateUtils'

/**
 * VERA-304 / VRG-337 — an arriving gift goes to the TOP of the list, and its
 * card animates in when it does.
 *
 * ## This hook no longer ORDERS anything — the server does
 *
 * It used to also return an `orderedCertificates` array that partitioned
 * incoming in-flight transfers to the front. That was removed in VERA-304 (A4),
 * for two independent reasons:
 *
 * 1. **It could not cross pagination, which is where the bug lived.** The list
 *    pages SERVER-side at 10 rows. A client-side hoist can only reorder the page
 *    it was handed, so an owner holding more than a page could receive a gift
 *    that landed on page 3 and never see it surface — refreshing did not help,
 *    because the row was never in `certificates` to begin with. Ordering is the
 *    only layer that can fix that: it decides which rows page 1 CONTAINS, and a
 *    reorder applied after the LIMIT is structurally too late. The pin now lives
 *    in the query's `ORDER BY` (`certificate.drizzle.repository.ts`, keyed off
 *    `pinIncomingTransfers`), with the caller's sort kept as the secondary key
 *    so nothing else about the order changes.
 * 2. **Two sorts fighting is how a list flickers.** With the server pinning, a
 *    client partition on top would be a second opinion about the same question,
 *    re-derived from a different predicate — and it WAS a different predicate
 *    (see below). Any disagreement shows up as a row that jumps position on the
 *    20s background poll, which is worse than the bug being fixed.
 *
 * The `isDefaultSort` guard moved with it: the server applies the pin only when
 * the request names no sort key, which is the same rule this hook held ("an
 * explicit sort is an instruction; silently overriding it with 'except these'
 * is how a sorted list stops being trustworthy").
 *
 * ## The predicate that survived, and the bug found in it
 *
 * What remains is arrival DETECTION, which drives the card's entrance animation
 * only — it never reorders. It now tests `isIncomingInFlightTransfer`, which
 * reads the TRANSFER record's status against the same four values the API's list
 * query admits an incoming transfer by.
 *
 * It previously tested the CERTIFICATE's status against
 * `IN_FLIGHT_TRANSFER_STATUSES` (`transfer_initiated`,
 * `transfer_waiting_confirmation`) — which omits `waiting_accept`, the state a
 * gift actually SITS in while its recipient is being waited on. So the flag
 * matched nothing for a real gift and the animation could never fire. Verified
 * against the dev database: every in-flight transfer there is
 * `waiting_accept` / `confirmation`, 4 of 4.
 */

/**
 * How long an arrival stays flagged.
 *
 * Longer than the animation (260ms) on purpose: the flag is cleared so a later
 * re-render cannot leave a stale `animation` on a card that settled minutes ago,
 * not to cut the motion short.
 */
const ARRIVAL_FLAG_MS = 1_200

export interface UseArrivingTransfersParams {
  /** The rows as the page renders them — i.e. in the order the server sent. */
  certificates: any[] | undefined
  /**
   * Identity of the CURRENT query: filters + tab + page, stringified.
   *
   * Load-bearing for arrival detection. A key change (filter, tab, search, page)
   * legitimately swaps the whole set, and every id in it is "new" — animating
   * those would flash the entire grid on every filter tap. So a key change
   * re-baselines silently and only a SAME-key change can produce an arrival,
   * which is exactly the 20s background poll (`useGetCertificates`).
   */
  queryKey: string
  /**
   * The rows on screen are the RESOLVED result for `queryKey` — pass
   * `!isLoading && !isPlaceholderData`.
   *
   * ⚠️ Without this, arrival detection is wrong in two ways, both of which flash
   * the grid at the user:
   *
   * 1. **First load.** `useCertificateFilters` returns `[]` for an unresolved
   *    query, so the first observation would baseline as "no pending transfers"
   *    and then, on the very next render with the same key, treat every pending
   *    gift the account already had as a fresh arrival. An account opening the
   *    page to three waiting gifts would watch all three fly in.
   * 2. **Filter/tab/page change.** `keepPreviousData` leaves the PREVIOUS key's
   *    rows mounted while the new set loads (`isPlaceholderData`), so those rows
   *    would be recorded against the new key. A pending gift that the old filter
   *    excluded and the new one includes would then read as having just arrived.
   *
   * While this is false the hook records nothing and animates nothing; the first
   * settled observation of a new key baselines silently.
   */
  settled: boolean
}

export const useArrivingTransfers = ({
  certificates,
  queryKey,
  settled,
}: UseArrivingTransfersParams) => {
  const profile = useSelector(({ auth }) => auth.profile)

  /** ids that appeared as incoming-pending since the previous poll of this key. */
  const [arrivingIds, setArrivingIds] = useState<Set<string>>(() => new Set())

  /** The previous observation: which key it belonged to, and its pending ids. */
  const seenRef = useRef<{ key: string; ids: Set<string> } | null>(null)
  const timersRef = useRef<Array<ReturnType<typeof setTimeout>>>([])

  useEffect(
    () => () => {
      timersRef.current.forEach(clearTimeout)
      timersRef.current = []
    },
    [],
  )

  useEffect(() => {
    if (!certificates || !settled) return

    const pendingIds = new Set<string>(
      certificates
        .filter(cert => isIncomingInFlightTransfer(cert, profile))
        .map(cert => String(cert.id)),
    )

    const previous = seenRef.current
    seenRef.current = { key: queryKey, ids: pendingIds }

    // FIRST PAINT (`previous === null`) and every KEY CHANGE re-baseline without
    // animating. First paint is the case the brief calls out: an account that
    // already has three pending gifts should not have all three fly in on load —
    // they did not just arrive, the user just opened the page.
    if (previous === null || previous.key !== queryKey) return

    const fresh = [...pendingIds].filter(id => !previous.ids.has(id))
    if (!fresh.length) return

    setArrivingIds(prev => {
      const next = new Set(prev)
      fresh.forEach(id => next.add(id))
      return next
    })

    const timer = setTimeout(() => {
      // Drop the fired timer rather than letting the list grow for the lifetime
      // of the session — this page is polled every 20s and stays open.
      timersRef.current = timersRef.current.filter(t => t !== timer)
      setArrivingIds(prev => {
        if (!fresh.some(id => prev.has(id))) return prev
        const next = new Set(prev)
        fresh.forEach(id => next.delete(id))
        return next
      })
    }, ARRIVAL_FLAG_MS)
    timersRef.current.push(timer)
    // `certificates` is a fresh array on every poll, which is what makes this run
    // once per poll. `profile` is in the deps because the recipient test depends
    // on it and it can resolve after the first render.
  }, [certificates, queryKey, profile, settled])

  return { arrivingIds }
}

export default useArrivingTransfers
